import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { formatMoney, RECURRING_FREQUENCY_LABELS } from "@finora/config";
import { RecurringFrequency, type ProductDto } from "@finora/types";
import { Button, EmptyState } from "@finora/ui";
import { FileStack } from "lucide-react";
import { ApiError } from "@finora/api-client";
import { api } from "../../lib/api";
import { ProductAutocomplete } from "../../components/ProductAutocomplete";
import { DateInputDDMMYYYY } from "../../components/DateInputDDMMYYYY";

interface StagedItem {
  key: string;
  productId: string;
  name: string;
  quantity: string;
  unitPriceGross: string;
  taxRateId: string;
  taxRatePercent: string;
}

export function RecurringInvoiceEditorPage() {
  const navigate = useNavigate();

  const [customerId, setCustomerId] = useState("");
  const [frequency, setFrequency] = useState<RecurringFrequency>(RecurringFrequency.MONTHLY);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState("");
  const [autoSend, setAutoSend] = useState(false);
  const [paymentTerms, setPaymentTerms] = useState("");
  const [items, setItems] = useState<StagedItem[]>([]);

  const { data: dashboard } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.getDashboard(),
  });
  const { data: customers } = useQuery({
    queryKey: ["customers"],
    queryFn: () => api.listCustomers(),
  });

  const create = useMutation({
    mutationFn: () =>
      api.createRecurringInvoiceTemplate({
        businessLocationId: dashboard!.defaultBusinessLocationId,
        customerId,
        frequency,
        startDate: new Date(startDate).toISOString(),
        endDate: endDate ? new Date(endDate).toISOString() : null,
        autoSend,
        paymentTerms: paymentTerms || null,
        items: items.map((item) => ({
          productId: item.productId,
          name: item.name,
          quantity: item.quantity,
          unitPriceGross: item.unitPriceGross,
          taxRateId: item.taxRateId,
        })),
      }),
    onSuccess: () => navigate("/ponavljajuce-fakture"),
  });

  function addProduct(product: ProductDto) {
    setItems((prev) => [
      ...prev,
      {
        key: `${product.id}-${prev.length}`,
        productId: product.id,
        name: product.name,
        quantity: "1",
        unitPriceGross: product.unitPriceGross,
        taxRateId: product.taxRateId,
        taxRatePercent: product.taxRate?.ratePercent ?? "0",
      },
    ]);
  }

  const total = items.reduce((sum, i) => sum + Number(i.quantity) * Number(i.unitPriceGross), 0);
  const canSubmit =
    !!customerId && items.length > 0 && !!startDate && !!dashboard?.defaultBusinessLocationId;

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <h1 className="text-xl font-semibold text-slate-900">Novi šablon ponavljajuće fakture</h1>

      <div className="grid grid-cols-1 gap-4 rounded-xl border border-border bg-surface p-5 shadow-subtle sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-red-600">Kupac *</label>
          <select
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          >
            <option value="">Izaberi kupca...</option>
            {customers?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-red-600">Učestalost *</label>
          <select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as RecurringFrequency)}
            className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          >
            {Object.values(RecurringFrequency).map((f) => (
              <option key={f} value={f}>
                {RECURRING_FREQUENCY_LABELS[f]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-red-600">Datum početka *</label>
          <DateInputDDMMYYYY value={startDate} onChange={setStartDate} />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">
            Datum završetka (opciono)
          </label>
          <DateInputDDMMYYYY value={endDate} onChange={setEndDate} />
        </div>

        <div className="sm:col-span-2">
          <label className="mb-1 block text-sm font-medium text-slate-700">Uslovi plaćanja</label>
          <input
            value={paymentTerms}
            onChange={(e) => setPaymentTerms(e.target.value)}
            placeholder="npr. Plaćanje u roku od 15 dana"
            className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>

        <label className="flex items-center gap-2 sm:col-span-2">
          <input
            type="checkbox"
            checked={autoSend}
            onChange={(e) => setAutoSend(e.target.checked)}
            className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
          />
          <span className="text-sm text-slate-700">
            Automatski pošalji fakturu čim se generiše (bez ručnog odobravanja)
          </span>
        </label>
      </div>

      <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
        <h2 className="mb-3 text-sm font-semibold text-slate-800">Stavke koje se ponavljaju</h2>

        <div className="mb-4">
          <ProductAutocomplete
            placeholder="Ukucaj ili klikni da izabereš artikal/uslugu..."
            onSelect={addProduct}
          />
        </div>

        {items.length === 0 ? (
          <EmptyState icon={FileStack} title="Nema dodatih stavki" description="Dodajte bar jednu stavku iznad." />
        ) : (
          <table>
            <thead>
              <tr>
                <th>Naziv</th>
                <th>Kol.</th>
                <th>Cena</th>
                <th>PDV</th>
                <th>Iznos</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={item.key}>
                  <td className="font-medium text-slate-800">{item.name}</td>
                  <td>
                    <input
                      value={item.quantity}
                      onChange={(e) => {
                        const next = [...items];
                        next[idx] = { ...item, quantity: e.target.value };
                        setItems(next);
                      }}
                      className="h-8 w-16 rounded-md border border-border px-2 text-sm focus:border-brand focus:outline-none"
                    />
                  </td>
                  <td>{formatMoney(item.unitPriceGross)}</td>
                  <td>{item.taxRatePercent}%</td>
                  <td className="font-medium">
                    {formatMoney((Number(item.quantity) * Number(item.unitPriceGross)).toFixed(2))}
                  </td>
                  <td>
                    <button
                      aria-label="Ukloni stavku"
                      onClick={() => setItems(items.filter((_, i) => i !== idx))}
                      className="text-slate-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {items.length > 0 && (
          <div className="mt-4 flex justify-end text-base font-semibold text-slate-900">
            Ukupno po fakturi: {formatMoney(total.toFixed(2))}
          </div>
        )}
      </div>

      {create.isError && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {create.error instanceof ApiError
            ? create.error.response.message
            : "Došlo je do greške prilikom čuvanja. Pokušajte ponovo."}
        </p>
      )}

      <div className="flex justify-end gap-3">
        <Button variant="secondary" onClick={() => navigate("/ponavljajuce-fakture")}>
          Otkaži
        </Button>
        <Button disabled={!canSubmit || create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? "Čuvanje..." : "Sačuvaj šablon"}
        </Button>
      </div>
    </div>
  );
}
